import {isAiInfographicHostRequest} from '../protocol/ai-infographics';
import {isSourceSceneHostRequest} from '../protocol/source-scene-host';
import {resetAiInfographicUi} from './compat/ai-infographic-reset';
import {isAiSceneModifyHostRequest} from '../protocol/ai-scene-modify-host';
import {isAiSceneHostRequest} from '../protocol/ai-scene-host';
import {matchesEditorInstance} from '../protocol/editor-handshake';
import {isImageEditRequest} from '../protocol/image-edit';
import {isCaptionRequest} from '../protocol/caption-request';
import {isCaptionTranslationHostRequest} from '../protocol/caption-translation-host';
import {EditorLoadState} from './EditorLoadState';
import {EDITOR_CONNECT_TIMEOUT_REASON,editorLoadFailureReason} from './editor-load-failure';
import { useEffect, useState, useRef, useCallback } from "react";
import type {SubscribeBrowserOutputAction,CompanyBrowserOutput,CompanyUploadedMedia,CompanyMediaAdded,SubscribeUploadedMedia} from '../protocol/work-session';
import {connectVideoSourceUpload,createVideoSourceUploadRpc} from './host/video-source-upload';
import {
  COMPANY_WORK_CHANNEL,
  readCompanyEditorServices,
  trustedCompanyHostOrigin,
  type CompanyEditorServices,
  type CompanyWorkSeed,
  type CompanySaveResult,
  type CompanySaveWork,
  type CompanySaveIntent,
} from "../protocol/work-session";
import {
  createNativeEnvelope,
  type NativeEditorEnvelope,
} from "../protocol/native-envelope";
import {
  readNativeDocumentFile,
  importedLegacyFile,
  requiresNativeCodeTrust,
  type NativeFileState,
} from "./compat/native-document-file";
import { migrateLegacyDocument } from "./compat/migrate-legacy-document";
import { measureMedia } from "./compat/measure-media";
import { EngineWorkbench } from "./EngineWorkbench";
import { z } from "zod";
import {prepareWorkBackupRestore} from './compat/work-backup-restore';
import {connectHostTransport} from './host/client';
import {isHostReadRequest} from '../protocol/host-read';

const legacyInventory = z.object({
  assets: z.record(
    z.object({ type: z.string(), remoteUrl: z.string().nullable() }),
  ),
});
async function prepare(seed: CompanyWorkSeed): Promise<NativeFileState> {
  let file = seed.file;
  let importNotice: string | undefined;
  if (!file) {
    const { assets } = legacyInventory.parse(seed.legacyDocument);
    const measured: Record<string, { width: number; height: number;durationInSeconds?:number|null }> = {};
    const media = Object.entries(assets).filter(([, a]) =>
      ["video", "image"].includes(a.type),
    );
    let index = 0;
    await Promise.all(
      Array.from({ length: 2 }, async () => {
        while (index < media.length) {
          const [id, asset] = media[index++]!;
          try {
            measured[id] = await measureMedia(asset.type, asset.remoteUrl ?? "");
          } catch (error) {
            throw new Error(asset.type==='video'?'영상 원본을 불러오지 못했습니다.':'이미지 원본을 불러오지 못했습니다.',{cause:error});
          }
        }
      }),
    );
    const converted = migrateLegacyDocument(seed.legacyDocument, measured);
    if (!converted.ok) throw new Error(converted.reason);
    const base = importedLegacyFile(
      converted.document,
      seed.legacyDocument,
      new Date().toISOString(),
    );
    importNotice = base.importNotice;
    file = createNativeEnvelope({
      ...base.envelope,
      scope: seed.scope,
      sourceSnapshotHash: seed.sourceSnapshotHash,
      sourceContext: seed.sourceContext,
    });
  }
  const read = readNativeDocumentFile(file);
  if (read.status !== "ready")
    throw new Error(
      read.status === "error" ? read.message : "편집 자료를 읽지 못했습니다.",
    );
  return {...read.value, revision:seed.revision, requiresInitialSave: !seed.file, ...(importNotice ? {importNotice} : {})};
}

export function LinkedWork() {
  const outputAction=useRef<((action:'render'|'publish')=>void)|null>(null);
  const subscribeBrowserOutputAction=useCallback<SubscribeBrowserOutputAction>(receive=>{outputAction.current=receive;return()=>{if(outputAction.current===receive)outputAction.current=null;};},[]);
  const mediaReceiver=useRef<((media:CompanyUploadedMedia)=>Promise<CompanyMediaAdded|void>)|null>(null);
  const mediaPort=useRef<MessagePort|null>(null);
  const subscribeUploadedMedia=useCallback<SubscribeUploadedMedia>(receive=>{
    mediaReceiver.current=receive;
    mediaPort.current?.postMessage({type:'media-ready',ready:true,browserOutputFormats:['mp4','webm']});
    return ()=>{if(mediaReceiver.current===receive){mediaReceiver.current=null;mediaPort.current?.postMessage({type:'media-ready',ready:false});}};
  },[]);
  const [publishBrowserOutput,setPublishBrowserOutput]=useState<CompanyBrowserOutput>();
  const [services,setServices]=useState<CompanyEditorServices>();
  const [file, setFile] = useState<NativeFileState | null>(null);
  const [trusted, setTrusted] = useState(false);
  const [engineVersion,setEngineVersion]=useState(0);
  const [recovery,setRecovery]=useState<{file:NativeFileState;trusted:boolean}|null>(null);
  const operationCount=useRef(0);
  const [operations,setOperations]=useState(0);
  const [loadFailed,setLoadFailed]=useState(false);
  const [loadFailureReason,setLoadFailureReason]=useState<string>();
  const [save, setSave] = useState<
    CompanySaveWork | null
  >(null);
  useEffect(() => {
    const hostOrigin = trustedCompanyHostOrigin(new URLSearchParams(window.location.search).get(
      "hostOrigin",
    ));
    const standalone=new URLSearchParams(window.location.search).get('standalone')==='1';
    let hostWindow:Window|null=window.parent!==window?window.parent:standalone?window.opener:null;
    const instanceId=crypto.randomUUID();
    let disposed = false,
      connected = false;
    let port: MessagePort | null = null;
    let disconnectHost:(()=>void)|null=null;
    let disconnectImageUpload:(()=>void)|null=null;
    let imageUpload:ReturnType<typeof createVideoSourceUploadRpc>|null=null;
    const outputPending=new Map<string,{resolve:(message:string)=>void;reject:(error:Error)=>void;timer:ReturnType<typeof setTimeout>;finish:()=>void}>();
    const apiPending=new Map<string,{resolve:(data:unknown)=>void;reject:(error:Error)=>void;timer:ReturnType<typeof setTimeout>}>();
    let sequence = 0;
    const connectTimer = setTimeout(() => {
      if (!connected && !disposed) {setLoadFailureReason(EDITOR_CONNECT_TIMEOUT_REASON);setLoadFailed(true);}
    }, 15000);
    const pending = new Map<
      string,
      {
        resolve: (value: CompanySaveResult) => void;
        timer: ReturnType<typeof setTimeout>;
        finish: ()=>void;
      }
    >();
    const beginOperation=()=>{
      operationCount.current++;if(!disposed)setOperations(operationCount.current);
      let finished=false;return()=>{if(finished)return;finished=true;operationCount.current--;if(!disposed)setOperations(operationCount.current);};
    };
    const receive = (event: MessageEvent) => {
      if(standalone&&!connected&&event.origin===hostOrigin&&event.source&&event.source!==window&&(!hostWindow||event.source===hostWindow)&&event.data?.type===`${COMPANY_WORK_CHANNEL}:probe`){
        hostWindow=event.source as Window;
        hostWindow.postMessage({type:`${COMPANY_WORK_CHANNEL}:ready`,instanceId},event.origin);
        return;
      }
      if (
        connected ||
        !matchesEditorInstance(instanceId,event.data?.instanceId) ||
        event.source !== hostWindow ||
        !hostWindow ||
        event.origin !== hostOrigin ||
        event.origin === window.location.origin ||
        event.data?.type !== COMPANY_WORK_CHANNEL ||
        !event.ports[0]
      )
        return;
      connected = true;
      // Retain only the scoped MessagePort before mounting any editable/code content.
      if(standalone)window.opener=null;
      clearTimeout(connectTimer);
      port = event.ports[0];
      const activePort = port;
      mediaPort.current=activePort;
      setServices(readCompanyEditorServices(event.data.services));
      setPublishBrowserOutput(()=> (input:Parameters<CompanyBrowserOutput>[0])=>new Promise<string>((resolve,reject)=>{
        if(disposed||outputPending.size){reject(Error('앞선 갤러리 저장을 확인해 주세요.'));return;}
        const id=`browser-output-${++sequence}`,finish=beginOperation();
        const timer=setTimeout(()=>{outputPending.delete(id);finish();reject(Error('갤러리 저장 응답을 확인하지 못했습니다. 다시 보관을 눌러 확인해 주세요.'));},120000);
        outputPending.set(id,{resolve,reject,timer,finish});
        try{activePort.postMessage({type:'browser-output',id,...input});}catch{outputPending.delete(id);clearTimeout(timer);finish();reject(Error('출력을 전달하지 못했습니다.'));}
      }));
      disconnectHost=connectHostTransport(request=>new Promise((resolve,reject)=>{
        if(disposed||(!isHostReadRequest(request)&&!isCaptionRequest(request)&&!isImageEditRequest(request)&&!isCaptionTranslationHostRequest(request)&&!isAiSceneHostRequest(request)&&!isAiSceneModifyHostRequest(request)&&!isAiInfographicHostRequest(request)&&!isSourceSceneHostRequest(request))){reject(new Error('이 편집 기능은 아직 와카 서비스에 연결되지 않았습니다.'));return;}
        if(apiPending.size>=2){reject(new Error('앞선 조회가 끝난 뒤 다시 시도해 주세요.'));return;}
        const id=`catalog-${++sequence}`;
        const timer=setTimeout(()=>{apiPending.delete(id);reject(new Error(isCaptionTranslationHostRequest(request)?'번역 응답을 확인하지 못했습니다. 새 번역을 시작하기 전에 같은 요청을 다시 조회해 주세요.':(isAiSceneHostRequest(request)||isAiSceneModifyHostRequest(request))?'AI 씬 응답을 확인하지 못했습니다. 새 요청을 만들기 전에 같은 요청을 다시 조회해 주세요.':request.path==='/__waka/image-edit'?'이미지 생성 응답을 확인하지 못했습니다. 처리가 계속될 수 있으니 자료함의 결과를 먼저 확인해 주세요.':'편집 자료 조회 시간이 초과됐습니다. 다시 시도해 주세요.'));},(request.path==='/__waka/transcribe'||request.path==='/__waka/image-edit'||isCaptionTranslationHostRequest(request)||isAiSceneHostRequest(request)||isAiSceneModifyHostRequest(request))?240000:request.path==='/__waka/bgm-file'?60000:20000);
        apiPending.set(id,{resolve,reject,timer});
        try{activePort.postMessage({type:'host-request',id,request});}catch{clearTimeout(timer);apiPending.delete(id);reject(new Error('편집 자료 조회를 전달하지 못했습니다.'));}
      }));
      imageUpload=createVideoSourceUploadRpc(message=>activePort.postMessage(message),beginOperation);
      disconnectImageUpload=connectVideoSourceUpload(input=>imageUpload!.upload(input));
      port.onmessage = (reply: MessageEvent) => {
        const data = reply.data;
        if(data?.type==='ai-infographics-reset'){resetAiInfographicUi();return;}
        if(data?.type==='browser-output-action'&&(data.action==='render'||data.action==='publish')){outputAction.current?.(data.action);return;}
        if(data?.type==='browser-output-saved'){
          const request=outputPending.get(data.id);if(!request)return;
          outputPending.delete(data.id);clearTimeout(request.timer);request.finish();
          if(data.ok===true)request.resolve('갤러리에 보관했습니다. 원본 편집 이어가기로 다시 열 수 있습니다.');
          else request.reject(Error(typeof data.message==='string'?data.message:'갤러리 저장 실패'));return;
        }
        if(imageUpload?.receive(data))return;
        if(data?.type==='host-response'&&typeof data.id==='string'){
          const request=apiPending.get(data.id);if(!request)return;apiPending.delete(data.id);clearTimeout(request.timer);
          if(data.ok===true)request.resolve(data.data);else request.reject(new Error(typeof data.message==='string'?data.message:'편집 자료를 불러오지 못했습니다.'));return;
        }
        if(data?.type==='uploaded-media' && typeof data.media?.id==='string'){
          const receive=mediaReceiver.current;
          const finish=beginOperation();
          void (async()=>{
            try {
              if(!receive)throw new Error('편집기가 아직 준비되지 않았습니다.');
              const added=await receive(data.media);
              if(!disposed)activePort.postMessage({type:'media-added',id:data.media.id,ok:true,...(added?{assetId:added.assetId}:{})});
            }catch(error){if(!disposed)activePort.postMessage({type:'media-added',id:data.media.id,ok:false,message:error instanceof Error?error.message:'파일을 추가하지 못했습니다.'});}
            finally{finish();}
          })();
          return;
        }
        if (data?.type !== "saved" || typeof data.id !== "string") return;
        const request = pending.get(data.id);
        if (!request || typeof data.result?.ok !== "boolean") return;
        pending.delete(data.id);
        clearTimeout(request.timer);
        request.finish();
        request.resolve(data.result);
      };
      setSave(
        () => (envelope: NativeEditorEnvelope,intent?:CompanySaveIntent) =>
          new Promise<CompanySaveResult>((resolve) => {
            if(disposed){resolve({ok:false,message:'작업 연결이 종료됐습니다.'});return;}
            const finish=beginOperation();
            const id = `save-${++sequence}`;
            const timer = setTimeout(() => {
              pending.delete(id);
              finish();
              resolve({
                ok: false,
                message:
                  "저장 결과를 확인하지 못했습니다. 편집 파일을 보관한 뒤 현재 작업을 다시 확인해 주세요.",
              });
            }, 30000);
            pending.set(id, { resolve, timer, finish });
            try{activePort.postMessage({ type: "save", id, file: envelope, ...(intent?{intent}:{}) });}
            catch{pending.delete(id);clearTimeout(timer);finish();resolve({ok:false,message:'저장 요청을 전달하지 못했습니다. 다시 시도해 주세요.'});}
          }),
      );
      setLoadFailed(false);setLoadFailureReason(undefined);
      void prepare(event.data.seed).then(
        (value) => {
          if (!disposed) setFile(value);
        },
        (error) => {
          if (!disposed) {
            if(import.meta.env.DEV)console.warn('[precision-load]',error instanceof Error?error.message.replace(/https?:\/\/\S+/g,'[media]'):'load failed');
            setLoadFailureReason(editorLoadFailureReason(error));setLoadFailed(true);activePort.postMessage({type:'editor-load-error'});
          }
        },
      );
    };
    window.addEventListener("message", receive);
    if (
      hostOrigin &&
      hostOrigin !== window.location.origin &&
      hostWindow
    )
      hostWindow.postMessage(
        { type: `${COMPANY_WORK_CHANNEL}:ready`, instanceId },
        hostOrigin,
      );
    return () => {
      disposed = true;
      for(const request of outputPending.values()){clearTimeout(request.timer);request.finish();request.reject(Error('작업 연결이 종료됐습니다.'));}outputPending.clear();
      disconnectImageUpload?.();imageUpload?.dispose();
      disconnectHost?.();for(const request of apiPending.values()){clearTimeout(request.timer);request.reject(new Error('작업 연결이 종료됐습니다.'));}apiPending.clear();
      mediaPort.current=null;
      clearTimeout(connectTimer);
      window.removeEventListener("message", receive);
      port?.close();
      for (const request of pending.values()) {
        clearTimeout(request.timer);
        request.finish();
        request.resolve({ ok: false, message: "작업 연결이 종료됐습니다." });
      }
    };
  }, []);
  if (!file || !save)
    return <EditorLoadState failed={loadFailed} reason={loadFailureReason} onRetry={()=>window.location.reload()} onBack={()=>{if(mediaPort.current){mediaPort.current.postMessage({type:'return-to-service'});return;}const origin=trustedCompanyHostOrigin(new URLSearchParams(window.location.search).get('hostOrigin'));const host=window.parent!==window?window.parent:window.opener;if(origin&&host)host.postMessage({type:`${COMPANY_WORK_CHANNEL}:return`},origin);}}/>;
  if (requiresNativeCodeTrust(file) && !trusted)
    return (
      <main className="p-8">
        <h1>이 작업에는 실행할 코드가 있습니다</h1>
        <p>
          코드는 별도 편집 화면에서 실행됩니다. 로그인 정보는 전달하지 않지만 이
          화면에서 현재 작업을 저장할 수 있으므로 출처를 신뢰하는 작업만 열어
          주세요.
        </p>
        <button onClick={() => setTrusted(true)}>
          출처를 확인했고 편집 계속하기
        </button>
        {recovery&&<button onClick={()=>{setFile(recovery.file);setTrusted(recovery.trusted);setRecovery(null);setEngineVersion(value=>value+1);}}>실행하지 않고 복원 전 편집으로 돌아가기</button>}
      </main>
    );
  return <EngineWorkbench key={engineVersion} nativeFile={file} saveWork={save} services={services} publishBrowserOutput={publishBrowserOutput} subscribeBrowserOutputAction={subscribeBrowserOutputAction} subscribeUploadedMedia={subscribeUploadedMedia}
    restoreBusy={operations>0} restoreRecovery={recovery?.file} onRestore={(next,previous)=>{
      if(operationCount.current>0)throw new Error('저장 또는 파일 처리를 마친 뒤 다시 복원해 주세요.');
      const checked=prepareWorkBackupRestore(next.envelope,file.envelope);
      setRecovery({file:previous,trusted});setTrusted(false);setFile(checked);setEngineVersion(value=>value+1);
    }}/>;
}
